import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { CAP, useSession } from "@/auth/session";
import { ActionRow } from "@/components/ActionRow";
import { Chip } from "@/components/Chip";
import { Drawer } from "@/components/Drawer";
import { DataTable, type DataCol } from "@/components/DataTable";
import { PageHead } from "@/components/PageHead";
import { SearchInput } from "@/components/inputs";
import { ErrorNote } from "@/components/ErrorNote";
import { products, type SpaceAllocationRow, type SpaceRow } from "@/api/client";
import { qk, useSpaces } from "@/modules/products/data";
import { ChoosePrompt, ProductSelect, ScopeBar, ScopeField, ScopeAction } from "@/modules/products/ScopeBar";
import { useKindParam } from "@/lib/hooks/useKindParam";
import { Link } from "react-router-dom";

/** Spaces — a product's partitions (Engineering, Board Production, Repair).
 *
 *  SIX PER SOFTWARE PRODUCT, one vocabulary (D-102, migration 0068):
 *  Engineering, Testing, Board Production, a production space that follows
 *  the product group (Battery / Vehicle / Asset), Deployment, Repair.
 *
 *  NO MODAL. Dattatray: *"Dont open a pop up for Add space. We need to keep
 *  the UI very similar how we manage Add organisation."* So "Add space"
 *  reveals a row inline, above the table it adds to, and the product comes
 *  from the scope bar rather than being asked for again.
 *
 *  AUTHORING IS SUPER ADMIN ONLY. `CAP.MANAGE_PRODUCTS` is held by no plan
 *  and no company role, so a customer sees the list and no controls. The
 *  server refuses independently — hiding a control is a courtesy, not a
 *  permission check.
 *
 *  THE SLUG IS NOT EDITABLE once created, and that is a refusal with a
 *  reason: a tool SENDS the slug, so re-pointing it means a bench asks for
 *  `eol` and silently gets nothing back. Archive and add instead.
 *
 *  ALLOCATION IS INHERITED, NOT GRANTED HERE (D-102): entitling a company to
 *  the product MATERIALISES every space, so there is no "allocate" button and
 *  there should not be one. What this page does own is the two operations that
 *  act on an existing allocation — DISABLE it, and read WHAT IT GRANTS a given
 *  company — which moved here from the organisation page (D-105) when that
 *  page stopped listing spaces. "Allocated to: N companies" opens them.
 *
 *  STILL NOT BUILT: richer roles. Today Orbit has three roles and per-user
 *  space access (Fabric's shape); Dattatray parked Operations / Production
 *  Engineer / Repairs as its own decision.
 */
export function SpacesPage() {
  const { can } = useSession();
  const mayAuthor = can(CAP.MANAGE_PRODUCTS);
  const [kind, setKind] = useKindParam();   // ?kind= arrives from the Products drawer
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<SpaceRow | null>(null);
  const q = useSpaces(kind);
  const qc = useQueryClient();

  const archive = useMutation({
    mutationFn: (s: SpaceRow) =>
      products.updateSpace(kind, s.space_id, { archived: !s.archived }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.spaces(kind) }),
  });

  const cols: DataCol<SpaceRow>[] = [
    { key: "name", priority: 1, label: "Space", width: 200, render: (s) => s.name },
    { key: "slug", priority: 2, label: "Slug", width: 170, mono: true, render: (s) => s.slug },
    {
      key: "state", priority: 2, label: "State", width: 120,
      render: (s) => s.archived
        ? <Chip tone="mute">archived</Chip>
        : <Chip tone="ok">active</Chip>,
    },
    {
      // A COUNT THAT OPENS SOMETHING. It used to be text, which made it a
      // plausible number with no behaviour behind it — and the operations it
      // summarises (disable an allocation, read what it grants) had no screen
      // at all after the organisation page stopped listing spaces (D-105).
      key: "alloc", label: "Allocated to", width: 150,
      render: (s) => s.allocations === 0
        ? <span className="cap">no companies</span>
        : <button className="btn ghost" onClick={() => setOpen(s)}>
            {s.allocations} {s.allocations === 1 ? "company" : "companies"}
          </button>,
    },
    {
      // NO NUMBER HERE, and that is a correction. Dattatray: *"why give number
      // of grants? Just give a link to manage permissions — because the number
      // of grants can be addition of grants across tools, so it does not make
      // sense."*
      //
      // He is right, and it is the same rule the whole module is built on: a
      // permission number means nothing outside the TOOL it belongs to
      // (D-89), so cluster's "12" was 7 from `uds_tool` added to 5 from
      // `eol_tool` — an addition of two different vocabularies. A total you
      // cannot act on is worse than no total, because it looks like one.
      key: "perms", label: "Permissions", width: 200,
      render: (s) => (
        <Link className="btn ghost"
          to={`/products/permissions?kind=${encodeURIComponent(kind)}&space=${s.space_id}`}>
          Manage permissions
        </Link>
      ),
    },
    // `as const` because this array literal is inside a conditional spread and
    // so has no contextual type -- without it `1` widens to `number` and fails
    // DataCol's `1 | 2 | 3`.
    ...(mayAuthor ? [{
      key: "act", priority: 1 as const, label: "", width: 110,
      render: (s: SpaceRow) => (
        <button className={s.archived ? "btn ghost" : "btn red"} disabled={archive.isPending}
          onClick={() => archive.mutate(s)}>
          {s.archived ? "Restore" : "Archive"}
        </button>
      ),
    }] : []),
  ];

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Products"
        title="Product spaces"
        desc="A product's partitions. Each grants a fixed set of tool permissions, and every company allocated the space inherits them."
      />

      <ScopeBar right={mayAuthor ? (
        <button className={adding ? "btn ghost" : "btn teal"}
          disabled={!kind} title={!kind ? "Choose a product first" : undefined}
          onClick={() => setAdding(!adding)}>
          {adding ? "Cancel" : "Add space"}
        </button>
      ) : undefined}>
        <ProductSelect value={kind} onChange={setKind} />
      </ScopeBar>

      {adding && <AddSpaceRow kind={kind} onDone={() => setAdding(false)} />}

      <ErrorNote error={archive.error} />

      {!kind ? <ChoosePrompt what="spaces" /> : (
        <>
        <div className="section-label">Spaces</div>
        <DataTable rows={q.data?.spaces ?? []} cols={cols} rowKey={(s) => s.space_id}
          empty={<div className="panel panel-pad cap">
            {q.isLoading ? "Loading…" : "No spaces defined for this product yet."}
          </div>} />
        </>
      )}

      {open && (
        <AllocationsDrawer kind={kind} space={open.name} spaceId={open.space_id}
          onClose={() => setOpen(null)} />
      )}
    </div>
  );
}

/** Which companies hold ONE space, and whether each hold is live (D-105).
 *
 *  THIS MOVED HERE FROM THE ORGANISATION PAGE. That page listed a company's
 *  allocated SPACES and Dattatray removed it — *"why do we see allocated
 *  spaces to a company? We should see allocated product and model, that is
 *  it!"* — which is right for that page: six rows per product that nobody
 *  chose and nobody edited from there.
 *
 *  But two real operations rode on that list, and removing it removed the only
 *  door to them: DISABLING an allocation, and reading WHAT A SPACE ACTUALLY
 *  GRANTS a given company. `set_allocation_enabled` and
 *  `effective_permissions` kept their service functions, their routes and
 *  their client methods and lost their screen — a full stack with no screen,
 *  which is the same failure as a screen with no behaviour and harder to spot.
 *
 *  It belongs on the product's side because the space does (D-102). And the
 *  Spaces table already showed "Allocated to: N companies" — a plausible count
 *  with nothing behind it. This is what that count now opens.
 */
function AllocationsDrawer({ kind, space, spaceId, onClose }: {
  kind: string; space: string; spaceId: string; onClose: () => void;
}) {
  const { can } = useSession();
  const mayAuthor = can(CAP.MANAGE_PRODUCTS);
  const qc = useQueryClient();
  const [grantsFor, setGrantsFor] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["products", "space-allocations", kind, spaceId],
    queryFn: () => products.spaceAllocations(kind, spaceId),
  });

  const toggle = useMutation({
    // THE OPPOSITE OF WHAT THE ROW HOLDS, read from the row rather than from
    // component state: two rows and one piece of state is how a toggle starts
    // reporting its neighbour's value.
    mutationFn: (a: SpaceAllocationRow) => products.setAllocation(a.cps_id, !a.enabled),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["products", "space-allocations", kind, spaceId] });
      void qc.invalidateQueries({ queryKey: qk.spaces(kind) });
    },
  });

  const rows = q.data?.allocations ?? [];

  return (
    <Drawer onClose={onClose} header={<>
      <div style={{ fontWeight: 600 }}>{space}</div>
      <div className="cap">{kind} · who holds this space</div>
    </>}>
      <ErrorNote error={toggle.error} />
      {q.isLoading ? <div className="cap">Loading…</div>
        : rows.length === 0 ? <div className="cap">No company holds this space yet.</div>
        : rows.map((a) => (
          <div key={a.cps_id} className="panel panel-pad" style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <strong>{a.company}</strong>
              <span className="mono cap">{a.company_id}</span>
              {!a.enabled && <Chip tone="mute">disabled</Chip>}
              {/* The allocation OUTLIVES the entitlement on purpose - deleting
                  it would cascade user access and every override - so without
                  this the row reads as access that grants something. */}
              {!a.holds_product && <Chip tone="warn">product not held</Chip>}
              {/* NO `marginLeft: auto` HERE. The count is metadata, not an
                  action, and a hand-rolled right-align sitting next to a
                  button row is exactly what `tests/ui` refuses - it cannot
                  see that the buttons below are already in an ActionRow. */}
              <span className="cap">
                {a.users_with_access} {a.users_with_access === 1 ? "person" : "people"}
              </span>
            </div>
            <ActionRow>
              <button className="btn ghost"
                onClick={() => setGrantsFor(grantsFor === a.cps_id ? null : a.cps_id)}>
                What it grants
              </button>
              {mayAuthor && (
                <button className={a.enabled ? "btn red" : "btn teal"}
                  disabled={toggle.isPending}
                  onClick={() => toggle.mutate(a)}>
                  {a.enabled ? "Disable" : "Enable"}
                </button>
              )}
            </ActionRow>
            {grantsFor === a.cps_id && <Grants cpsId={a.cps_id} />}
          </div>
        ))}
    </Drawer>
  );
}

/** What this company effectively gets in this space, and WHICH layer decided.
 *
 *  BLOCKED IS NOT OFF. A permission the chain granted and a gate then blocked
 *  must not render identically to one nobody ticked — they are different
 *  answers, and the cause of the first is a row on another page. So the view
 *  shows `granted` / `off` / `blocked` from `granted` + `effective`, never
 *  from `effective` alone, and names the gate when there is one.
 */
function Grants({ cpsId }: { cpsId: string }) {
  const q = useQuery({
    queryKey: ["products", "effective", cpsId],
    queryFn: () => products.effectivePermissions(cpsId),
  });
  if (q.isLoading) return <div className="cap" style={{ marginTop: 10 }}>Loading…</div>;
  const d = q.data;
  if (!d) return <ErrorNote error={q.error} />;
  return (
    <div style={{ marginTop: 10 }}>
      {d.blocked_because && <Chip tone="warn">{d.blocked_because}</Chip>}
      <table className="tbl" style={{ marginTop: 8, width: "100%", tableLayout: "fixed" }}>
        <tbody>
          {d.permissions.map((p) => (
            <tr key={p.perm_uid}>
              <td>{p.name}</td>
              <td className="cap" style={{ width: 110 }}>{p.from}</td>
              <td style={{ width: 90 }}>
                {p.granted && !p.effective
                  ? <Chip tone="warn">blocked</Chip>
                  : p.effective ? <Chip tone="ok">granted</Chip> : <Chip tone="mute">off</Chip>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function AddSpaceRow({ kind, onDone }: { kind: string; onDone: () => void }) {
  const qc = useQueryClient();
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const add = useMutation({
    mutationFn: () => products.addSpace(kind, { slug, name, description: description || null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.spaces(kind) });
      void qc.invalidateQueries({ queryKey: qk.products });
      onDone();
    },
  });

  return (
    <div className="panel panel-pad" style={{
      display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap",
      marginBottom: 12,
    }}>
      <ScopeField label="Slug" hint="what the tool sends - permanent">
        <SearchInput value={slug} autoFocus mono placeholder="board_production"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSlug(e.target.value.toLowerCase().replace(/\s+/g, "_"))} />
      </ScopeField>
      <ScopeField label="Name" hint="what a person reads">
        <SearchInput value={name} placeholder="Board Production"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
      </ScopeField>
      <ScopeField label="Description" hint="optional">
        <SearchInput value={description}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)} />
      </ScopeField>
      <ScopeAction>
        <button className="btn teal" disabled={!slug || !name || add.isPending}
          onClick={() => add.mutate()}>
          {add.isPending ? "Adding…" : "Add space"}
        </button>
      </ScopeAction>
      <ErrorNote error={add.error} />
    </div>
  );
}
